import { onBeforeUnmount, onMounted, ref, type Ref } from 'vue'

// Text-like fields only: date/time/colour pickers, ranges and checkboxes raise no soft keyboard
// worth hiding the tab bar for. A missing type attribute is a text input.
const EDITABLE = [
    'textarea',
    '[contenteditable]:not([contenteditable="false"])',
    'input:not([type])',
    ...['text', 'search', 'email', 'url', 'tel', 'password', 'number'].map(type => `input[type="${type}" i]`),
].join(', ')

// How far the visual viewport must fall below the layout viewport to count as a soft keyboard.
// Larger than the URL-bar / browser-chrome collapse deltas (~50–120px), smaller than any
// phone soft keyboard (~250px+).
const KEYBOARD_MIN_PX = 150

function isEditable(element: Element | null): boolean {
    return Boolean(element && typeof element.matches === 'function' && element.matches(EDITABLE))
}

// Without visualViewport there is no way to see the keyboard; trust the focus.
function keyboardShown(): boolean {
    const viewport = window.visualViewport
    return viewport ? window.innerHeight - viewport.height > KEYBOARD_MIN_PX : true
}

// "Keyboard up" on a phone = a text field has focus AND the visual viewport has shrunk.
// Recomputed from real state rather than latched on focus: Android's Back button closes the
// keyboard without blurring, and a hardware keyboard or a return from background keeps focus
// with no soft keyboard. The phone bottom group hides while true. Listeners install after
// mount: nothing runs on the server.
export function useKeyboardOpen(): Ref<boolean> {
    const isOpen = ref(false)
    let pending: ReturnType<typeof setTimeout> | null = null

    function update(): void {
        isOpen.value = isEditable(document.activeElement) && keyboardShown()
    }

    function cancelPending(): void {
        if (pending !== null) clearTimeout(pending)
        pending = null
    }

    // During focusout document.activeElement is still the element losing focus.
    function onFocusOut(): void {
        cancelPending()
        pending = setTimeout(() => {
            pending = null
            update()
        }, 0)
    }

    onMounted(() => {
        document.addEventListener('focusin', update)
        document.addEventListener('focusout', onFocusOut)
        window.visualViewport?.addEventListener('resize', update)
        update()
    })

    onBeforeUnmount(() => {
        document.removeEventListener('focusin', update)
        document.removeEventListener('focusout', onFocusOut)
        window.visualViewport?.removeEventListener('resize', update)
        cancelPending()
    })

    return isOpen
}
